import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Text, Button, Modal } from '@rsos/base-components';
import {
  ModalFooter,
  ModalBody,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { useDeletePreconfiguredMessageMutation } from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import type { PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { AppState } from '../../../types';
import { trackDeletePreconfiguredResponse } from '../preconfiguredMessagesTrackings';
import { DELETE_SUCCESS_MESSAGE } from './utils';

interface Props {
  isOpened: boolean;
  onClose: () => void;
  message: PreconfiguredResponse;
}

const DeleteMessageModal: React.FC<Props> = ({
  isOpened,
  onClose,
  message,
}: Props) => {
  const orgName =
    useSelector((state: AppState) => organizationSelector(state))?.name || '';
  const [deletePreconfiguredMessage, { isLoading: isDeleting }] =
    useDeletePreconfiguredMessageMutation();

  const handleDelete = useCallback(() => {
    trackDeletePreconfiguredResponse(message);
    deletePreconfiguredMessage({ org: orgName, id: message.id })
      .unwrap()
      .then(() => {
        notifySuccess(DELETE_SUCCESS_MESSAGE);
      })
      .catch((error: unknown) => {
        const err = error as { data?: { message?: string } };
        notifyError(err.data?.message || 'Failed to delete message');
      })
      .finally(() => {
        onClose();
      });
  }, [orgName, message, onClose, deletePreconfiguredMessage]);

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      width="350px"
      id="modal-delete-preconfigured-message"
      data-name="delete-message-modal"
    >
      {message && (
        <>
          <ModalBody onClose={onClose} height="100%">
            <Text
              size="xlarge"
              color="primaryText"
              data-name="delete-message-title"
            >
              Delete {message.title}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Box horizontal flow={12} width="100%">
              <Button
                btnType="tertiary"
                onClick={onClose}
                grow
                noMargin
                dataName="cancel-delete-button"
              >
                Cancel
              </Button>
              <Button
                btnType="warning_primary"
                onClick={handleDelete}
                grow
                noMargin
                isLoadingProp={isDeleting}
                dataName="confirm-delete-button"
              >
                Delete
              </Button>
            </Box>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default DeleteMessageModal;
